import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import { idColumn } from './ui/idColumn';
import { useExport } from './header/exporters';
import { useLoad } from '../hooks/useLoad';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useRole } from '../hooks/useRole';
import { setOrdersBar, useProcurementBar } from '../hooks/useProcurementBar';
import { fetchOrders, PROCUREMENT_REFRESHED_EVENT } from '../services/ProcurementService';
import { formatDecimal, formatCurrencyTotals, currencyTotalLines, sumByCurrency } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate } from '../utils/formatDate';
import { fmtQty, ORDER_STATUS_LABELS, ORDER_STATUS_VARIANT, PAYMENT_LABELS, RECEIVING_LABELS } from './procurement/procurementLabels';
import type { OrderRow } from '../types/Procurement';
import '../styles/components/ProcurementScreens.css';

const IN_TRANSIT = new Set(['domestic-transit', 'shipped']);

/** Whether one order matches the header search (order number, seller, marketplace, notes). */
export const orderMatches = (o: OrderRow, term: string): boolean => {
  if (!term) return true;
  return [o.marketplace_order_id, o.seller_name, o.marketplace, o.shipping_agent_name, o.notes]
    .some(v => (v ?? '').toLowerCase().includes(term));
};

const ProcurementTracker: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const { orders: bar } = useProcurementBar();
  const search = useHeaderSearch().trim().toLowerCase();

  const { data, loading, error, reload, setData } = useLoad(
    () => fetchOrders({ lifecycle: bar.archived ? 'archived' : 'active' }),
    [bar.archived],
  );

  // The auto-refresh lands new active orders; a failed refresh never reaches here (it is only logged).
  useEffect(() => {
    if (bar.archived) return undefined;
    const onRefreshed = (e: Event) => {
      const orders = (e as CustomEvent<{ orders?: OrderRow[] }>).detail?.orders;
      if (Array.isArray(orders)) setData(orders);
    };
    window.addEventListener(PROCUREMENT_REFRESHED_EVENT, onRefreshed);
    return () => window.removeEventListener(PROCUREMENT_REFRESHED_EVENT, onRefreshed);
  }, [bar.archived, setData]);

  const rows = data ?? [];
  const matchesFilters = (o: OrderRow) => (
    (!bar.status || o.status === bar.status)
    && (!bar.receiving || o.receiving_status === bar.receiving)
    && (!bar.payment || o.payment_status === bar.payment)
  );
  const shown = useMemo(
    () => rows.filter(o => matchesFilters(o) && orderMatches(o, search)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, bar.status, bar.receiving, bar.payment, search],
  );

  useExport('procurement-orders', { rows: shown, ctx: undefined });

  const count = (pred: (o: OrderRow) => boolean) => rows.filter(pred).length;
  const openValue = sumByCurrency(
    rows.filter(o => !['draft', 'cancelled', 'delivered'].includes(o.status)),
    o => ({ amount: o.total_amount, currency: o.currency }),
  );

  if (error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="procurement orders" error={error} onRetry={reload} retrying={loading} /></div>;
  }

  const columns: Column<OrderRow>[] = [
    idColumn<OrderRow>('procurementOrder'),
    { key: 'marketplace_order_id', header: 'Order', sortable: true },
    { key: 'seller_name', header: 'Seller', sortable: true },
    { key: 'order_date', header: 'Ordered', sortable: true, render: v => (v ? formatDate(v, '') || v : '—') },
    {
      key: 'status', header: 'Status', sortable: true,
      render: (_v, o) => <StatusBadge variant={ORDER_STATUS_VARIANT[o.status] ?? 'neutral'}>{ORDER_STATUS_LABELS[o.status] ?? o.status}</StatusBadge>,
    },
    {
      key: 'receiving_status', header: 'Received', sortable: true,
      sortValue: o => o.receiving_status,
      render: (_v, o) => `${fmtQty(o.quantity_received)} / ${fmtQty(o.quantity_ordered)} (${RECEIVING_LABELS[o.receiving_status] ?? o.receiving_status})`,
    },
    { key: 'shipment_count', header: 'Shipments', sortable: true, align: 'right' },
    // Money columns are left out entirely for someone who may not see money.
    ...(canViewFinancials ? [
      { key: 'total_amount', header: 'Total', sortable: true, align: 'right' as const, render: (_v: unknown, o: OrderRow) => formatDecimal(o.total_amount, o.currency) },
      { key: 'landing_cost', header: 'Est. landed cost', align: 'right' as const, render: (_v: unknown, o: OrderRow) => formatDecimal(o.landing_cost, o.currency) },
      { key: 'payment_status', header: 'Payment', sortable: true, render: (_v: unknown, o: OrderRow) => (o.payment_status ? PAYMENT_LABELS[o.payment_status] : '—') },
    ] : []),
  ];

  return (
    <div className="wt-container fade-in">
      <section className="stats-grid">
        <MetricCard label={bar.archived ? 'Archived orders' : 'All orders'} value={rows.length} tone="pink" icon="package" active={!bar.status} onClick={() => setOrdersBar({ status: '' })} />
        <MetricCard label="Draft" value={count(o => o.status === 'draft')} tone="blue" icon="document" active={bar.status === 'draft'} onClick={() => setOrdersBar({ status: 'draft' })} />
        <MetricCard label="Ordered" value={count(o => o.status === 'ordered')} tone="purple" icon="clock" active={bar.status === 'ordered'} onClick={() => setOrdersBar({ status: 'ordered' })} />
        <MetricCard label="In transit" value={count(o => IN_TRANSIT.has(o.status))} tone="orange" icon="truck" />
        <MetricCard label="At warehouse" value={count(o => o.status === 'at-warehouse')} tone="teal" icon="package" active={bar.status === 'at-warehouse'} onClick={() => setOrdersBar({ status: 'at-warehouse' })} />
        <MetricCard label="Delivered" value={count(o => o.status === 'delivered')} tone="green" icon="check" active={bar.status === 'delivered'} onClick={() => setOrdersBar({ status: 'delivered' })} />
        {canViewFinancials && <MetricCard label="Open order value" value={formatCurrencyTotals(openValue)} lines={currencyTotalLines(openValue)} tone="red" icon="wallet" />}
      </section>

      {!loading && shown.length === 0 ? (
        <EmptyState
          icon="package"
          title={rows.length === 0 ? (bar.archived ? 'No archived orders' : 'No orders yet') : 'No orders match these filters'}
          description={rows.length === 0 && !bar.archived ? 'Orders placed with sellers and marketplaces for this project show up here.' : undefined}
          action={canEdit && rows.length === 0 && !bar.archived ? <button type="button" className="primary-btn" onClick={() => navigate('/procurement/orders/new')}>New order</button> : undefined}
        />
      ) : (
        <DataTable<OrderRow>
          recordList={{ kind: 'procurementOrder' }}
          data={shown}
          columns={columns}
          loading={loading}
          emptyMessage="No orders"
          pagination={{ enabled: true, pageSize: 25 }}
          onRowClick={o => navigate(entityPath('procurementOrder', o.id) ?? '/procurement/orders')}
        />
      )}
    </div>
  );
};

export default ProcurementTracker;
